import { assign, createMachine } from "xstate";

export type Context = {
    criticalResources: null | Set<string>, // load ressources that must be available for it to not fail 
    error: string | null; 
    attempts: number,
    maxAttempts: number 
}

export enum Phase {
  Validating = "validating",
  Loading = "loading",
  Preloading = "preloading",
  Done = "done",
  Error = "error",
}

export type Events =
  | { type: "VALIDATE"; criticalResources: Set<string> } // Added VALIDATE event with criticalResources
  | { type: "PRELOAD"; }
  | { type: "LOAD"; }
  | { type: "RETRY" }

export const loadMachine = createMachine({
  types: {} as {
    context: Context;
    events: Events;
  },
  /** @xstate-layout N4IgpgJg5mDOIC5QAoC2BDAxgCwJYDswBKAOgDd0AbXCdAFwKgGIBlAVQGEOBRFlgbQAMAXUSgADgHtYuBpPxiQAD0QAmAMwA2EgE5NgnQBYAHIM0B2AKzrVg9QBoQAT0TqzJQYdXXDARl+alpY66gC+oY5oWHiEpBTUtAz4zABiAIIAkgAyQqJIIFIycgr5KgjqJMaWhjqClpoN5m5Wxo4uCKq+Fea+tQ1anup+5uGRGDgExORUNPSMTOnZ-L55EtKyuPKKZYIk6uo6qqaG1erGOr7GqlZtiCeGJKqGbvW+JgFGoyBRE7EklJJ0BB5uwuLwBCJFIUNltSogesYSOYQpdfN4audWs5XF0kbVkcZLgZzMYwhFvuMYlMAUD5oscpD8tDits7mddNY3oFguZDE1bh1cT0+poBs9hl8flTSDTgckFpkcisoesWXCEM9LCRnlZOkNeXUGgL7o9noa3oTNJ9yVLJqRxAAnMCykGcHh8XIqoqbEqgMr+TokNEHXwkjQnbwCtzaTzeIb+bkhSWUu0kR3OwFy1KKz1M1U+1kIKq+R6BI6is7VSwCvVI3pmUX6cWh5PRVPpl3y+nLVYFfOwv2IYx8pE1BoBzS2IbG6qml6aC0fQyGcLk-CSCBwRS22JemG+5SIAC0I4agmFVYXgiuArR2gOC+CblqfMsqlbvym8VmSSge7Vg7lIiVwaPUehWlYBxRkc2pxoY+gLv4VSWB+0r-JmjD-gW6omKoJAWD0viCOehGdJoUZaEGTRXmiQTqOYmioe2Tqdn+ebegOh5FqSHhBNylZooS0GIl4PgIf4lwoTaKZ-BA8hgFhnH+uo-iVPRlxHAxGi+NB5hBoSyEMZo5yTkxfxgA6DqSA6ikHspaJ7MOdTIgYqiTsa7hvOYbmhocTS8quoRAA */
    initial: "validating",
    context: {
      criticalResources: null,
      error: null,
      attempts: 0,
      maxAttempts: 3,
    },
    states: {
      [Phase.Validating]: {
        on: {
          VALIDATE: {
            target: "loading",
            actions: ["reset", "setCritical"],
          },
          RETRY: [
            {
              target: "validating",
              guard: "canRetry",
              actions: { type: "incWithError", params: { label: "validation" } },
            },
            {
              target: "error",
              actions: { type: "fatal", params: { label: "validation" } },
            },
          ],
        },
      },

      [Phase.Loading]: {
        on: {
          LOAD: {
            target: "preloading",
            actions: "reset",
          },
          RETRY: [
            {
              target: "loading",
              guard: "canRetry",
              actions: { type: "incWithError", params: { label: "loading" } },
            },
            {
              target: "error",
              actions: { type: "fatal", params: { label: "loading" } },
            },
          ],
        },
      },

      [Phase.Preloading]: {
        on: {
          PRELOAD: {
            target: "done",
            actions: "reset",
          },
          RETRY: [
            {
              target: "preloading",
              guard: "canRetry",
              actions: { type: "incWithError", params: { label: "preloading" } },
            },
            {
              target: "error",
              actions: { type: "fatal", params: { label: "preloading" } },
            },
          ],
        },
      },

      [Phase.Done]: {
        type: "final",
      },

      [Phase.Error]: {
        type: "final",
      },
    },
  },
  {
    actions: {
      reset: assign({
        attempts: 0,
        error: null,
      }),

      setCritical: assign({
        criticalResources: ({ event, context }) => {
          if (event.type !== "VALIDATE" || !event.criticalResources) {
            return context.criticalResources;
          }
          return event.criticalResources;
        },
      }),

      incWithError: assign(((args: any, params?: { label?: string }) => {
        const nextAttempts = args.context.attempts + 1;
        return {
          attempts: nextAttempts,
          error: `Attempt ${nextAttempts} failed during ${params?.label ?? "unknown"}.`,
        };
      }) as any),

      fatal: assign(((args: any, params?: { label?: string }) => ({
        attempts: args.context.maxAttempts,
        error: `Fatal error: ${params?.label ?? "unknown"}.`,
      })) as any),
    },
    guards: {
      canRetry: ({ context }) => context.attempts + 1 < context.maxAttempts  // increment with one to simulate what happens after the retry
    },
  }
);
